import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import {
  MetricAverageResponseTimeWhereInput,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import {
  CardMainTimeValue,
  emptyValuePlaceholder,
  GenericSmallCard,
} from 'src/components/pages/dashboard/components/shared';
import { useTimeHelpers } from 'src/hooks/use-time-helpers';

export const GET_METRIC_MEDIAN_RESPONSE_TIME_QUERY = gql`
  query GetMetricAverageResponseTime(
    $institutionId: Int!
    $period: MetricPeriodWhereInput!
    $comparePeriod: MetricComparePeriodInput
    $where: MetricAverageResponseTimeWhereInput
  ) {
    metricAverageResponseTime(
      institutionId: $institutionId
      period: $period
      comparePeriod: $comparePeriod
      where: $where
    ) {
      total
      previousPeriod {
        total
        trendChangePercentage
        trendChangeIndicator
      }
      lastUpdatedAt
    }
  }
`;

export const MedianResponseTimeCard = () => {
  const { t } = useTranslation(['dashboard']);
  const { formatAbbreviatedComposedDuration } = useTimeHelpers();

  const abbreviatedDurationTransformer = useCallback(
    (value: number | null | undefined) =>
      whenDefined(value, (value) =>
        formatAbbreviatedComposedDuration({
          inputInSeconds: value,
          defaultValue: '0',
        }),
      ) ?? emptyValuePlaceholder,
    [formatAbbreviatedComposedDuration],
  );

  return (
    <GenericSmallCard<
      Pick<Query, 'metricAverageResponseTime'>,
      MetricAverageResponseTimeWhereInput
    >
      title={t('dashboard:cards.medianResponseTime.title')}
      query={GET_METRIC_MEDIAN_RESPONSE_TIME_QUERY}
      dataResolver={(result) => {
        const metricData = result.data?.metricAverageResponseTime;

        if (!metricData) {
          return undefined;
        }

        const { lastUpdatedAt, total = null, previousPeriod } = metricData;

        return {
          lastUpdatedAt,
          total,
          previousPeriod: previousPeriod
            ? {
                trendChangePercentage: previousPeriod?.trendChangePercentage,
                trendChangeIndicator: previousPeriod?.trendChangeIndicator,
                total: previousPeriod?.total ?? 0,
              }
            : null,
        };
      }}
      whereResolver={({ targetUserId }) => ({
        dimensionParticipatingUser: { id: { _eq: targetUserId } },
      })}
      valueTransformer={(value = null) => (
        <CardMainTimeValue value={value === 0 ? null : value} />
      )}
      previousValueTextTransformer={abbreviatedDurationTransformer}
      tooltipValueTransformer={(value) =>
        whenDefined(value, (value) =>
          formatAbbreviatedComposedDuration({
            inputInSeconds: value,
            defaultValue: '0',
            unitsToBeUsed: 4,
          }),
        )
      }
      cardInfoI18nKey="dashboard:cards.medianResponseTime.tooltipDescription"
      testId="dashboard-median-response-time-card"
    />
  );
};
